import { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import axios from '../axiosConfig';
import i18n from '../i18n';
import { STRIPE_PUBLIC_KEY, API_ENDPOINTS } from '../utils/constants';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

/**
 * Custom hook for subscription API operations
 * Handles all subscription-related API calls
 */
export const useSubscriptionApi = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Clear current error
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Fetch available subscription plans from database.
     * Returns plans with full details including FREE plan.
     * @returns {Promise<Array>} Formatted plans array
     */
    const fetchPlans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(API_ENDPOINTS.PLANS);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            // Format plans from API response
            const formattedPlans = response.data.map(plan => ({
                id: plan.id,
                code: plan.code,
                priceId: plan.priceId,
                name: plan.name,
                description: plan.description,
                price: plan.monthlyPrice ? parseFloat(plan.monthlyPrice) : 0,
                currency: plan.currency?.toLowerCase() || 'pln',
                period: 'month',
                features: plan.features || [],
                recommended: plan.recommended || false,
                maxDogs: plan.maxDogs,
                sortOrder: plan.sortOrder,
                isCustom: plan.isCustom,
                isFree: !plan.monthlyPrice || parseFloat(plan.monthlyPrice) === 0
            }));

            return formattedPlans;
        } catch (err) {
            console.error('Error fetching plans:', err);
            const errorMessage = err.response?.status === 429
                ? i18n.t('subscription:api.tooManyRequests')
                : i18n.t('subscription:api.fetchPlansFailed');
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Create checkout session and redirect to Stripe
     * @param {string} priceId - Stripe price ID
     * @returns {Promise<void>}
     */
    const createCheckoutSession = useCallback(async (priceId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CREATE_CHECKOUT, {
                priceId,
                quantity: 1
            });

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            const { sessionId, url } = response.data;

            // Redirect to Stripe checkout
            if (url) {
                window.location.href = url;
            } else if (sessionId) {
                const stripe = await stripePromise;
                const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });
                if (stripeError) {
                    throw new Error(stripeError.message);
                }
            }
        } catch (err) {
            console.error('Error creating checkout session:', err);
            const errorMessage = err.response?.status === 429
                ? i18n.t('subscription:api.tooManyAttempts')
                : err.message || i18n.t('subscription:api.processRequestFailed');
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Change subscription plan
     * @param {string} priceId - New price ID
     * @returns {Promise<Object>} Response data
     */
    const changePlan = useCallback(async (priceId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CHANGE_PLAN, {
                priceId,
                enableProration: true
            });

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error changing plan:', err);
            const errorMessage = err.response?.status === 429
                ? i18n.t('subscription:api.tooManyAttempts')
                : err.message || i18n.t('subscription:api.changePlanFailed');
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Unified upgrade to paid plan.
     * Backend decides whether to create checkout (for FREE users) or change plan directly (for paid users).
     * If checkout is needed, automatically redirects to Stripe checkout.
     * @param {string} priceId - Target price ID
     * @returns {Promise<Object>} Response data with action taken
     */
    const upgradePlan = useCallback(async (priceId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.UPGRADE_PLAN, {
                priceId
            });

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            const { action, checkoutUrl, sessionId, message, scheduledChangeDate } = response.data;

            // If backend says checkout is needed, redirect to Stripe
            if (action === 'CHECKOUT' && checkoutUrl) {
                window.location.href = checkoutUrl;
                return { action, redirecting: true };
            }

            // Return result with action type and any scheduled change date
            return { action, message, scheduledChangeDate };
        } catch (err) {
            console.error('Error upgrading plan:', err);
            const errorMessage = err.response?.status === 429
                ? i18n.t('subscription:api.tooManyAttempts')
                : err.response?.data?.error || err.message || i18n.t('subscription:api.changePlanFailed');
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Verify checkout session and update subscription if completed.
     * Called when user returns from Stripe checkout to verify the payment went through.
     * @param {string} sessionId - Stripe checkout session ID
     * @returns {Promise<Object>} Response with status (SUCCESS or PENDING)
     */
    const verifyCheckout = useCallback(async (sessionId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.VERIFY_CHECKOUT, {
                sessionId
            });

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error verifying checkout:', err);
            const errorMessage = err.response?.data?.error || err.message || i18n.t('subscription:api.verifyPaymentFailed');
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Cancel subscription
     * @returns {Promise<Object>} Response data
     */
    const cancelSubscription = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CANCEL);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error cancelling subscription:', err);
            setError(err.message || i18n.t('subscription:api.cancelFailed'));
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Open Stripe customer portal
     * @returns {Promise<void>}
     */
    const openCustomerPortal = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CUSTOMER_PORTAL);

            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (err) {
            console.error('Error opening customer portal:', err);
            setError(i18n.t('subscription:api.openPortalFailed'));
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Start free trial
     * @param {string} priceId - Optional Stripe price ID for the plan to trial.
     *                           If provided, trial will enforce the selected plan's owner limits.
     * @returns {Promise<Object>} Response data
     */
    const startTrial = useCallback(async (priceId = null, promoCode = null) => {
        try {
            setLoading(true);
            setError(null);

            const requestBody = {};
            if (priceId) requestBody.priceId = priceId;
            if (promoCode) requestBody.promoCode = promoCode;
            const response = await axios.post(API_ENDPOINTS.START_TRIAL, requestBody);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error starting trial:', err);

            let errorMessage;
            if (err.response?.status === 429) {
                errorMessage = i18n.t('subscription:api.tooManyAttemptsPolite');
            } else if (err.response?.status === 400) {
                errorMessage = err.response?.data?.error || i18n.t('subscription:api.startTrialBadRequest');
            } else {
                errorMessage = err.response?.data?.error || i18n.t('subscription:api.startTrialFailed');
            }

            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Activate the FREE subscription plan.
     * Unlike startTrial, this:
     * - Sets status to ACTIVE (not TRIAL)
     * - Has no end date (permanent)
     * - Doesn't consume the trial (user can still trial paid plans later)
     * @returns {Promise<Object>} Response data
     */
    const activateFree = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.ACTIVATE_FREE);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error activating free subscription:', err);

            let errorMessage;
            if (err.response?.status === 429) {
                errorMessage = i18n.t('subscription:api.tooManyAttemptsPolite');
            } else if (err.response?.status === 400) {
                errorMessage = err.response?.data?.error || i18n.t('subscription:api.activateFreeBadRequest');
            } else {
                errorMessage = err.response?.data?.error || i18n.t('subscription:api.activateFreeFailed');
            }

            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Fetch usage information (owner count and limits)
     * @returns {Promise<Object>} Usage data
     */
    const fetchUsage = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(API_ENDPOINTS.USAGE);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error fetching usage:', err);
            const errorMessage = err.response?.status === 429
                ? i18n.t('subscription:api.tooManyRequestsRetry')
                : i18n.t('subscription:api.fetchUsageFailed');
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Check if dog can be added
     * @returns {Promise<Object>} Can add dog status
     */
    const checkCanAddDog = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(API_ENDPOINTS.CAN_ADD_DOG);

            return response.data;
        } catch (err) {
            console.error('Error checking can add dog:', err);

            // If 403, parse the dog limit exceeded response
            if (err.response?.status === 403) {
                const errorData = err.response.data?.errors || {};
                return {
                    canAddDog: false,
                    currentDogs: parseInt(errorData.currentDogs) || 0,
                    maxDogs: parseInt(errorData.maxDogs) || 0,
                    planName: errorData.planName || i18n.t('subscription:api.unknownPlan'),
                    upgradeRequired: true,
                    message: errorData.dogLimit || i18n.t('subscription:api.dogLimitReached')
                };
            }

            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Validate a promo code
     * @param {string} code - The promo code to validate
     * @returns {Promise<Object>} { valid, code, bonusDays, message }
     */
    const validatePromoCode = useCallback(async (code) => {
        try {
            const response = await axios.post(API_ENDPOINTS.VALIDATE_PROMO_CODE, { code });
            return response.data;
        } catch (err) {
            console.error('Error validating promo code:', err);
            return { valid: false, message: i18n.t('subscription:api.validatePromoFailed') };
        }
    }, []);

    return {
        loading,
        error,
        clearError,
        fetchPlans,
        createCheckoutSession,
        changePlan,
        upgradePlan,
        verifyCheckout,
        cancelSubscription,
        openCustomerPortal,
        startTrial,
        activateFree,
        fetchUsage,
        checkCanAddDog,
        validatePromoCode
    };
};

export default useSubscriptionApi;
